import { cn } from '@/lib/utils';

type ConnectivityState = 'Online' | 'Joined' | 'Intermittent' | 'Offline';

const dotColor: Record<ConnectivityState, string> = {
  Online: 'bg-success-500',
  Joined: 'bg-success-500',
  Intermittent: 'bg-warning-500',
  Offline: 'bg-error-500',
};

const textColor: Record<ConnectivityState, string> = {
  Online: 'text-success-700 dark:text-success-400',
  Joined: 'text-success-700 dark:text-success-400',
  Intermittent: 'text-warning-700 dark:text-warning-400',
  Offline: 'text-error-700 dark:text-error-400',
};

interface ConnectivityInputs {
  lastSeenAt?: string | null;
  joinedAt?: string | null;
}

// Joined fills the gap between LoRaWAN join (~1s after power-on) and the first
// uplink (~60s after). Without it, freshly-joined devices read as Offline on
// every view that watches lastSeenAt.
export function getConnectivityState({ lastSeenAt, joinedAt }: ConnectivityInputs): ConnectivityState {
  if (lastSeenAt) {
    const minutesAgo = (Date.now() - new Date(lastSeenAt).getTime()) / 60000;
    if (minutesAgo <= 60) return 'Online';
    if (minutesAgo <= 1440) return 'Intermittent'; // 24 hours
    return 'Offline';
  }
  if (joinedAt) return 'Joined';
  return 'Offline';
}

interface ConnectivityStatusProps extends ConnectivityInputs {
  className?: string;
}

export function ConnectivityStatus({ lastSeenAt, joinedAt, className }: ConnectivityStatusProps) {
  const state = getConnectivityState({ lastSeenAt, joinedAt });
  return (
    <span className={cn('inline-flex items-center gap-1.5 text-sm font-medium', textColor[state], className)}>
      <span className={cn('w-2 h-2 rounded-full', dotColor[state])} />
      {state}
    </span>
  );
}
